import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DSBackLink, DSIcon } from '../design-system/primitives';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import { websocketService } from '../services/websocketService';
import { showToast } from '../components/Toast';
import { ProgressBar, MaintenanceHistoryList, MaintenanceAssignSheet } from '../components/MaintenanceUi';
import { TicketRow } from '../components/TicketUi';
import ReportIssueIntake from '../components/ReportIssueIntake';
import '../components/MaintenanceUi.css';
import '../components/TicketUi.css';
import './MaintenanceRecordDetail.css';

// Screen 2 of the Machine Maintenance feature (docs/MACHINE_MAINTENANCE_REQUIREMENTS.md
// §6/§8, design reference: MaintenanceScreens.tsx MaintenanceRecordScreen).
// Routed by machineId (not recordId) - loading this page is itself the lazy
// get-or-create trigger for the machine's current record (§6).

function MaintenanceRecordDetail() {
  const { machineId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const isOrganizer = isAdmin || (currentUser?.featureRoles || []).includes('machine_maintenance');
  const techTier = currentUser?.techTier ?? currentUser?.tech_tier ?? 0;
  const viewerId = currentUser?.id;

  // This page is reachable from the member list, the Organizer Dashboard, the
  // Machine Detail Maintenance tab and an issue's "Found during maintenance"
  // link, so a hardcoded back target strands anyone who didn't come from the
  // list. Same returnTo/returnLabel convention this page already passes down
  // to MachineIssueDetail. Falls back to the list for a cold/deep link.
  const backTo = location.state?.returnTo || '/machines/maintenance';
  const backLabel = location.state?.returnLabel || 'Back to Maintenance';

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [busyItemId, setBusyItemId] = useState(null);
  const [claiming, setClaiming] = useState(false);

  const [showAssignSheet, setShowAssignSheet] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignableUsersLoading, setAssignableUsersLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [openIssues, setOpenIssues] = useState([]);
  const [openIssuesLoading, setOpenIssuesLoading] = useState(true);

  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [submittingIssue, setSubmittingIssue] = useState(false);
  const [issueSubmitSuccess, setIssueSubmitSuccess] = useState(false);
  const [issueSubmitError, setIssueSubmitError] = useState('');

  // `silent` skips the full-page loading/error state - used for background
  // refreshes (self-heal after a conflict, live updates from other users)
  // where flashing the whole checklist to a spinner would be jarring.
  const loadRecord = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const response = await apiService.getCurrentMaintenanceRecord(machineId);
      setRecord(response.data);
      if (!silent) setError('');
    } catch (err) {
      if (!silent) setError('Failed to load this machine\'s maintenance checklist.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [machineId]);

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const response = await apiService.getMaintenanceHistory(machineId);
      setHistory(response.data || []);
    } catch (err) {
      // Non-critical - the current checklist above still works without it.
      console.error('Failed to load maintenance history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, [machineId]);

  const loadOpenIssues = useCallback(async () => {
    try {
      setOpenIssuesLoading(true);
      const response = await apiService.getMachineIssues({ status: 'open', machineId });
      setOpenIssues(response.data || []);
    } catch (err) {
      // Non-critical - the checklist above still works without it.
      console.error('Failed to load open issues:', err);
    } finally {
      setOpenIssuesLoading(false);
    }
  }, [machineId]);

  useEffect(() => {
    loadRecord();
    loadHistory();
    loadOpenIssues();
  }, [loadRecord, loadHistory, loadOpenIssues]);

  // Maintenance day means several people can have this same checklist open
  // at once - without this, a conflicting action from someone else is only
  // discovered the next time you try (and fail) to do something yourself
  // (see handleToggleItem's self-heal below). This keeps everyone's screen
  // current without waiting for that.
  useEffect(() => {
    const unsubscribe = websocketService.on('maintenance_record_changed', (data) => {
      if (Number(data?.machineId) === Number(machineId)) {
        loadRecord({ silent: true });
        loadHistory();
      }
    });
    return unsubscribe;
  }, [machineId, loadRecord, loadHistory]);

  const currentUserName = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ')
    || currentUser?.username || '';

  const handleSubmitIssue = async ({ description, gameUnplayable, affectsTournamentPlay }) => {
    try {
      setSubmittingIssue(true);
      setIssueSubmitError('');

      await apiService.reportMachineIssue(machineId, {
        reporterName: currentUserName,
        description,
        gameUnplayable,
        affectsTournamentPlay,
        maintenanceRecordId: record.id
      });

      setIssueSubmitSuccess(true);
    } catch (err) {
      setIssueSubmitError('Failed to submit issue report. Please try again.');
      console.error(err);
    } finally {
      setSubmittingIssue(false);
    }
  };

  const handleReportIssueDone = () => {
    setIssueSubmitSuccess(false);
    setShowReportIssueModal(false);
    loadOpenIssues();
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelfClaim = async () => {
    setClaiming(true);
    try {
      const response = await apiService.claimMaintenanceRecord(machineId);
      setRecord(response.data);
      showToast("You're on this.", 'success');
    } catch (err) {
      showToast('Failed to self-claim this machine.', 'error');
    } finally {
      setClaiming(false);
    }
  };

  const handleSelfUnclaim = async () => {
    setClaiming(true);
    try {
      const response = await apiService.unclaimMaintenanceRecord(machineId);
      setRecord(response.data);
      showToast('You\'re no longer working on this.', 'info');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update this machine.', 'error');
      // Someone else may have already cleared or taken the claim - resync
      // rather than leaving a button that keeps failing.
      loadRecord({ silent: true });
    } finally {
      setClaiming(false);
    }
  };

  const handleReleaseAssignment = async () => {
    setClaiming(true);
    try {
      const response = await apiService.releaseMaintenanceRecord(machineId);
      setRecord(response.data);
      showToast('Assignment released. A Maintenance Organizer can reassign it.', 'info');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to release this assignment.', 'error');
      loadRecord({ silent: true });
    } finally {
      setClaiming(false);
    }
  };

  // Organizers assign from here as well as from the Organizer Dashboard, so
  // the member list is fetched lazily on first open - it's a ~400-row payload
  // that the vast majority of this page's viewers never need.
  const openAssignSheet = async () => {
    setShowAssignSheet(true);
    if (assignableUsers.length > 0) return;
    try {
      setAssignableUsersLoading(true);
      const response = await apiService.getMaintenanceAssignableUsers();
      setAssignableUsers(response.data || []);
    } catch (err) {
      showToast('Failed to load the member list.', 'error');
    } finally {
      setAssignableUsersLoading(false);
    }
  };

  const handleAssign = async (userId) => {
    setAssigning(true);
    try {
      const response = await apiService.assignMaintenanceRecord(machineId, userId);
      setRecord(response.data);
      showToast(userId ? 'Assignment updated.' : 'Assignment removed.', 'success');
      setShowAssignSheet(false);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update assignment.', 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleToggleItem = async (item) => {
    setBusyItemId(item.id);
    try {
      let response;
      if (item.completed_at) {
        response = await apiService.uncompleteMaintenanceItem(item.id);
        showToast(`Reverted "${item.title}".`, 'info');
      } else {
        // No success toast here - the checklist's own big green checkbox
        // already confirms completion, and a toast per item gets in the way
        // when checking off several items in a row.
        response = await apiService.completeMaintenanceItem(item.id);
      }
      setRecord(response.data);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update this item.', 'error');
      // Self-heal: a failure here often means someone else already changed
      // this item (e.g. "Item is already complete") - reload so the item's
      // checkbox reflects what actually happened instead of staying stuck
      // showing stale, still-clickable state that just fails again on retry.
      loadRecord({ silent: true });
    } finally {
      setBusyItemId(null);
    }
  };

  if (loading) {
    return <div className="maintenance-record maintenance-ui bn-page-padding"><p className="mr-loading">Loading…</p></div>;
  }

  if (error || !record) {
    return <div className="maintenance-record maintenance-ui bn-page-padding"><div className="error-message">{error || 'Checklist not found.'}</div></div>;
  }

  const items = record.items || [];
  const total = items.length;
  const done = items.filter((i) => i.completed_at).length;
  const allDone = total > 0 && done === total;
  const isAssignedToViewer = record.assigned_to !== null && record.assigned_to === viewerId;
  const isSelfClaimedByViewer = record.self_claimed_by !== null && record.self_claimed_by === viewerId;
  const canCompleteItems = isAdmin || techTier >= 2 || isAssignedToViewer;

  return (
    <div className="maintenance-record maintenance-ui">
      <div className="mr-header">
        <DSBackLink to={backTo}>{backLabel}</DSBackLink>
        <div className="mr-header__top">
          <h1 className="mr-header__title">{record.machine_name}</h1>
          {isOrganizer && (
            <button type="button" className="mr-assign-btn" onClick={openAssignSheet}>
              {record.assigned_to ? 'Reassign' : 'Assign'}
            </button>
          )}
        </div>
        <ProgressBar done={done} total={total} complete={allDone} />
      </div>

      <div className="mr-body">
        {isAssignedToViewer && (
          <div className="mr-banner mr-banner--assigned">
            <span className="mr-banner__icon"><DSIcon name="dot" /></span>
            <div className="mr-banner__body-col">
              <p className="mr-banner__title">Assigned to you</p>
              <p className="mr-banner__body">A Maintenance Organizer assigned this machine's checklist to you.</p>
            </div>
            <button type="button" className="mr-release-btn" onClick={handleReleaseAssignment} disabled={claiming}>
              Release
            </button>
          </div>
        )}

        {record.assigned_to && !isAssignedToViewer && (
          <div className="mr-note-row">
            <span className="mr-note-row__icon"><DSIcon name="dotOutline" /></span>
            <p className="mr-note-row__text">
              Assigned to <strong>{record.assigned_to_name}</strong>
            </p>
          </div>
        )}

        {!record.assigned_to && (
          <div className="mr-claim-row">
            {isSelfClaimedByViewer ? (
              <div className="mr-claim-row__self">
                <span className="mr-claim-row__dot" />
                <p>You're working on this</p>
                <button type="button" className="mr-unclaim-btn" onClick={handleSelfUnclaim} disabled={claiming}>
                  Stop working on this
                </button>
              </div>
            ) : record.self_claimed_by_name ? (
              <div className="mr-note-row">
                <span className="mr-note-row__icon mr-note-row__icon--claimed" />
                <p className="mr-note-row__text">
                  <strong>{record.self_claimed_by_name}</strong> is working on this. You can still complete any item.
                </p>
                {/* Organizers can clear a stale claim left behind by someone
                    who walked away without dropping it themselves. */}
                {isOrganizer && (
                  <button type="button" className="mr-unclaim-btn" onClick={handleSelfUnclaim} disabled={claiming}>
                    Clear
                  </button>
                )}
              </div>
            ) : (
              (techTier >= 2 || isAdmin) && (
                <button type="button" className="mr-claim-btn" onClick={handleSelfClaim} disabled={claiming}>
                  <DSIcon name="dotOutline" /> Mark yourself as working on this
                </button>
              )
            )}
          </div>
        )}

        {allDone && (
          <div className="mr-banner mr-banner--done">
            <span className="mr-banner__icon"><DSIcon name="check" /></span>
            <div>
              <p className="mr-banner__title mr-banner__title--done">All done, record complete!</p>
              <p className="mr-banner__body">This machine is serviced for the cycle.</p>
              {record.credits?.length > 0 && (
                <p className="mr-banner__body">
                  Points: {record.credits
                    .map((c) => `${c.user_id === viewerId ? 'You' : c.user_name} +${c.points_awarded}`)
                    .join(', ')}
                </p>
              )}
            </div>
          </div>
        )}

        {!allDone && canCompleteItems && record.point_value > 0 && (
          <div className="mr-note-row">
            <span className="mr-note-row__icon"><DSIcon name="star" /></span>
            <p className="mr-note-row__text">
              Finishing every item earns <strong>{record.point_value} points</strong>, split evenly (rounded up) between everyone who checks one off.
            </p>
          </div>
        )}

        {techTier < 2 && !isAdmin && !isAssignedToViewer && (
          <div className="mr-view-only">
            <p className="mr-view-only__title">View only</p>
            <p className="mr-view-only__body">
              You need a Maintenance Organizer assignment to complete items on this record.
              Tier 2+ members can complete items freely.
            </p>
          </div>
        )}

        <div className="mr-issues-section ticket-ui">
          <div className="mr-issues-section__header">
            <p className="mr-checklist-heading">Open Issues ({openIssues.length})</p>
            <button type="button" className="mr-report-issue-btn" onClick={() => setShowReportIssueModal(true)}>
              Report an Issue
            </button>
          </div>
          {!openIssuesLoading && openIssues.length === 0 && (
            <p className="mr-issues-section__empty">No open issues for this machine.</p>
          )}
          {openIssues.length > 0 && (
            <div className="mr-issues-section__rows">
              {openIssues.map((issue) => (
                <TicketRow
                  key={issue.id}
                  issue={issue}
                  onPress={(i) => navigate(`/machine-issues/${i.id}`, {
                    state: { returnTo: `/machines/maintenance/${machineId}`, returnLabel: 'Back to Maintenance' }
                  })}
                  viewerTier={techTier}
                  viewerIsAdmin={isAdmin}
                  showMachineName={false}
                />
              ))}
            </div>
          )}
        </div>

        <p className="mr-checklist-heading">Checklist</p>
        <div className="mr-checklist">
          {items.map((item) => {
            const isDone = !!item.completed_at;
            const expanded = expandedIds.has(item.id);
            const busy = busyItemId === item.id;

            return (
              <div key={item.id} className={`mr-item ${isDone ? 'mr-item--done' : ''}`}>
                <div className="mr-item__row">
                  <button
                    type="button"
                    className="mr-item__checkbox"
                    onClick={() => handleToggleItem(item)}
                    disabled={!canCompleteItems || busy}
                    aria-label={isDone ? `Mark ${item.title} incomplete` : `Complete ${item.title}`}
                  >
                    <span className={`mr-item__checkbox-circle ${isDone ? 'mr-item__checkbox-circle--done' : ''}`}>
                      {isDone && <DSIcon name="check" />}
                    </span>
                  </button>
                  <button type="button" className="mr-item__title-btn" onClick={() => toggleExpand(item.id)}>
                    <span className="mr-item__title-text">
                      <span className={`mr-item__title ${isDone ? 'mr-item__title--done' : ''}`}>{item.title}</span>
                      {isDone && (
                        <span className="mr-item__meta">
                          {item.completed_by === viewerId ? 'You' : item.completed_by_name} · {new Date(item.completed_at).toLocaleDateString()}
                        </span>
                      )}
                    </span>
                    <span className={`mr-item__chevron ${expanded ? 'mr-item__chevron--open' : ''}`}><DSIcon name="chevronDown" /></span>
                  </button>
                </div>

                {expanded && (
                  <div className="mr-item__details">
                    {item.instructions && (
                      <div>
                        <p className="mr-item__details-label">Instructions</p>
                        <p className="mr-item__details-text">{item.instructions}</p>
                      </div>
                    )}
                    {item.rationale && (
                      <div>
                        <p className="mr-item__details-label">Why this matters</p>
                        <p className="mr-item__details-text">{item.rationale}</p>
                      </div>
                    )}
                    {canCompleteItems && !isDone && (
                      <p className="mr-item__details-hint">
                        See a problem with this machine? Use "Report an Issue" above instead of a note here.
                      </p>
                    )}
                    {canCompleteItems && (
                      <button
                        type="button"
                        className={`mr-item__action ${isDone ? 'mr-item__action--revert' : 'mr-item__action--complete'}`}
                        onClick={() => handleToggleItem(item)}
                        disabled={busy}
                      >
                        {isDone ? 'Revert completion' : <><DSIcon name="check" /> Mark complete</>}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {(() => {
          const pastRecords = history.filter((r) => r.id !== record.id);
          if (historyLoading || pastRecords.length === 0) return null;

          return (
            <>
              <p className="mr-checklist-heading">History ({pastRecords.length})</p>
              <MaintenanceHistoryList records={pastRecords} viewerId={viewerId} />
            </>
          );
        })()}
      </div>

      {showAssignSheet && (
        <MaintenanceAssignSheet
          machineName={record.machine_name}
          users={assignableUsers}
          loading={assignableUsersLoading}
          currentAssignedTo={record.assigned_to}
          busy={assigning}
          onAssign={handleAssign}
          onClose={() => setShowAssignSheet(false)}
        />
      )}

      {showReportIssueModal && (
        <div className="modal-overlay report-issue-modal" onClick={() => setShowReportIssueModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowReportIssueModal(false)}><DSIcon name="x" /></button>
            {!issueSubmitSuccess && <h2>Report an Issue</h2>}
            <ReportIssueIntake
              reporterName={currentUserName}
              reporterNameReadOnly
              machineName={record.machine_name}
              submitting={submittingIssue}
              error={issueSubmitError}
              success={issueSubmitSuccess}
              onSubmit={handleSubmitIssue}
              onDone={handleReportIssueDone}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintenanceRecordDetail;
